import React from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import CatalogContext from "../../../../contexts/CatalogContext";
import { useProposalContext } from "../../../../hooks/useProposalContext";
import ApiKeyContext from "../../../../contexts/ApiKeyContext";

import QuantityInput from "./QuantityInput";
import NotesInput from "./NotesInput";
import ProposalItem from "../../../../models/ProposalItem";

import { processImagePath, trackEvent } from "../../../../util/Functions";

import "./ProposalItemActions.scss";
import { AxiosError } from "axios";
import { SidebarContentType, useSidebarContext } from "../../../../hooks/useSidebarContext";

export default function ProposalItemActions({ item, mode, inOrderIcon, savedInOrderIcon }) {
	const Dialog = withReactContent(Swal);
	const [catalog] = React.useContext(CatalogContext);
	const access = catalog.options?.proposals?.access;
	const {
		currentProposal,
		flag,
		setCurrentProposal,
		fetchAndSetCurrent,
		createNewProposal,
		forceUpdateRerenderFlag,
		setAwaitingProposalItem,
	} = useProposalContext();

	const API_KEY_CONTEXT = React.useContext(ApiKeyContext);
	//only customers invited to the catalog should be able to create a proposal / order
	//if a user's api-key is 1, then that means they're not a customer to the catalog.
	const isCustomer = API_KEY_CONTEXT.apiKey !== 1;

	const image = item.media && item.media?.find((m) => m.type === "image")?.source;
	const thumb = image && processImagePath(image, "thumb");

	const SidebarContext = useSidebarContext();
	const [isSaving, setIsSaving] = React.useState(false);
	const [addingToProposal, setAddingToProposal] = React.useState(undefined);

	let proposalItem = currentProposal?.items?.find(cur => +(cur.item?.id || cur.item) === +item.id);
	if (proposalItem && proposalItem?.constructor.name !== "ProposalItem") {
		proposalItem = proposalItem.self;
	}

	if (!proposalItem) {
		proposalItem = new ProposalItem();
		proposalItem.item = item.id;
		proposalItem.proposal = currentProposal?.id;
	}

	/*
		TODO !!!!!!!!!!! faking it until stones have Inventory records and item has Item.inventory_dependent and Item.total_inventory
		don't check inventory now because NULL should be considered infinite whereas 0 is no inventory
		maybe some sort of "can oversell" flag can be added if inventory becomes mandatory
	 */
	const isStone = !!proposalItem.item?.gemstone;
	const maximumQuantity = isStone ? 1 : undefined;
	const isSingleUse = maximumQuantity === 1;

	const [quantityDisplay, setQuantityDisplay] = React.useState(proposalItem?.quantity);
	
	React.useEffect(() => {
		setQuantityDisplay(proposalItem.quantity);
	}, [proposalItem?.quantity]);

	const selectOrCreateNewProposal = async (text = undefined) => {
		const result = await Dialog.fire({
			html: text ?? "",
			icon: "warning",
			showCloseButton: true,
			showDenyButton: true,
			confirmButtonText: "Create New",
			denyButtonText: `Select Existing`,
			denyButtonColor: '#09f'
		});
		
		if (result.isDismissed) {
			return false;
		}
		else if (result.isConfirmed) {
			return await createNewProposal();
		} else if (result.isDenied) {
			SidebarContext.open(SidebarContentType.Proposal);
			setAwaitingProposalItem(proposalItem);
		}
	};

	// Find proposal item
	const setProperty = async (property, value) => {
		/** @type Proposal */
		let selectedProposal = currentProposal;

		if (!selectedProposal) {
			const selectedOrCreatedProposal = await selectOrCreateNewProposal("Please select or create a proposal first.");
			if (selectedOrCreatedProposal) {
				selectedProposal = selectedOrCreatedProposal;
				proposalItem.proposal = selectedProposal.id;
			}
			else {
				// closed or select existing
				return;
			}
		}
		
		if (selectedProposal?.approved) {
			selectOrCreateNewProposal(`Approved Order <strong>${selectedProposal.name}</strong> cannot be modified. Please create a new Order or select an existing one.`);
			return;
		}

		if (selectedProposal?.consented) {
			const result = await Dialog.fire({
				html: `If you modify items in the <strong>${selectedProposal.name}</strong> order, you will need to resubmit proposal request order.`,
				icon: "warning",
				showCloseButton: true,
				showDenyButton: true,
				confirmButtonText: "Continue Modifying",
				denyButtonText: `Create or Select Different`,
				denyButtonColor: '#09f'
			});
			if (result.isConfirmed) {
				selectedProposal.self.setProperty("consented", false);
				selectedProposal.self.saveProperty("consented").then(() => {
					fetchAndSetCurrent(selectedProposal.id);
					SidebarContext.open(SidebarContentType.Proposal);
				});
			} else if (result.isDenied) {
				selectOrCreateNewProposal("Please create new or select from existing proposals first.");
				return;
			}
			return;
		}

		// If quantity 0, prompt to remove item
		const confirmedRemoved = property === "quantity" && +value === -1;
		property === "quantity" && trackEvent("Proposal", value ? "add" : "remove", item.common_sku);

		if (confirmedRemoved) {
			try {
				await currentProposal.removeItem(proposalItem);
			}
			catch (e) {
				if (!(e instanceof AxiosError && e.response.status === 404)) {
					console.error("Failed to remove item", e);
				}
			}

			currentProposal.items = currentProposal.items.filter(i => i.id !== proposalItem.id);
			setCurrentProposal(currentProposal);
		}
		else {
			proposalItem.setProperty(property, value);

			if (!proposalItem.id) {
				setIsSaving(true);
				try {
					await proposalItem.save();
					selectedProposal.addItem(proposalItem);
					setCurrentProposal(selectedProposal);
				} catch (e) {
					console.error("Failed to add to proposal", e);
				}
				setIsSaving(false);
			}
			else {
				try {
					await proposalItem.saveProperty(property);
				} catch (e) {
					if (!(e instanceof AxiosError && e.response.status === 404)) {
						console.error("Failed to remove from proposal", e);
					}
				}
			}
		}

		forceUpdateRerenderFlag();
	}

	const addToProposal_click = React.useCallback(async () => {
		setAddingToProposal(true);
		const quantity = proposalItem.quantity ? +proposalItem.quantity : 0;

		const updatedQuantity = isSingleUse && quantity ? 0 : (quantity + 1);
		await setProperty("quantity", updatedQuantity);

		setQuantityDisplay(proposalItem.quantity);
		setAddingToProposal(false);
	}, [proposalItem, currentProposal]);


	if (mode === "button") {
		let label = quantityDisplay ? `In Order (${quantityDisplay})` : "Add to Order";
		if (isSingleUse) {
			label = proposalItem.id && !quantityDisplay ? "Saved in Order" : quantityDisplay === 1 ? "In Order" : "Add to Order";
		}

		return (
			<div
				data-component="ProposalItemActions"
				data-type="button"
				className={[addingToProposal && "processing"].filter(f=>f).join(" ")}
			>
				{isCustomer &&
					<Button
						type="button"
						variant="primary"
						className="add-to-proposal"
						onClick={addToProposal_click}
						quantity={quantityDisplay}
					>
						{isSingleUse && !!quantityDisplay && inOrderIcon && (inOrderIcon)}
						{isSingleUse && proposalItem.id && !quantityDisplay && savedInOrderIcon && (savedInOrderIcon)}
						{label}
					</Button>
				}
				{!isCustomer &&
					<OverlayTrigger overlay={<Tooltip className="_boss tooltip-info">Only customers in this catalog can create proposals. You are currently not a customer in this catalog.</Tooltip>}>
						<Button
							type="button"
							variant="primary"
							className="add-to-proposal"
						>
							{label}
						</Button>
					</OverlayTrigger>
				}
			</div>
		);
	}

	let proposalItemActions = '';
	const isLocked = (access === "view") || (currentProposal?.approved);
	if (access && (access !== "view") && (item.sell_price || item["data:sell_price"])) {

		if (isCustomer) {
			proposalItemActions = (
				<>
					<QuantityInput
						key={"qty" + proposalItem?.quantity + flag}
						value={proposalItem?.quantity}
						onChange={e => setProperty("quantity", e)}
						isLocked={isLocked}
						maximumAmount={maximumQuantity}
					/>
					<NotesInput
						key={"note" + proposalItem.id + flag}
						value={proposalItem?.notes}
						title={
							proposalItem?.item?.name ||
							proposalItem?.item?.description ||
							proposalItem?.item?.common_sku
						}
						isLocked={isLocked}
						image={thumb}
						onChange={e => setProperty("notes", e)}
					/>
				</>
			);
		}
		else {
			proposalItemActions = (
				<OverlayTrigger overlay={<Tooltip className="_boss tooltip-info">Only customers in this catalog can create proposals. You are currently not a customer in this catalog.</Tooltip>}>
					<div className="quantity-input">
						<button type="button" aria-label="Add to Order" value="+" data-field="quantity"
							className="button-plus border-0 rounded-circle icon-shape icon-sm"></button>
					</div>
				</OverlayTrigger>
			)
		}

	}

	const classes = [
		'proposalItemActions',
		currentProposal && "hasProposal",
		(proposalItem.quantity !== undefined) && "on-proposal",
		isSaving && "isSaving"
	].filter(f=>f);
	return (
		<div data-component="ProposalItemActions" className={classes.join(' ')}>
			{proposalItemActions}
		</div>
	);
}
